import { Project } from "../interfaces/Project";
import { Result } from "./types";
import { fetchData, fetchDataStrict } from "../clients/api";
import { handleError } from "../errors";
import { Estimate } from "../interfaces/Estimate";
import { Alternate } from "../interfaces/Alternate";
import { CacheHandle, defineCachedResourceById } from "./helpers";

const fetchAllProjects = async (): Promise<Result<Project[]>> => {
  try {
    const projectData = await fetchData({ table: "projects", params: { populate: ["region"] } });
    const activeProjects =
      projectData?.data?.filter((project: Project) => !project.archived && !project.deleted_at) || [];
    return { success: true, data: activeProjects };
  } catch (error) {
    const err = handleError(error, { table: "projects", context: "fetchAllProjects" });
    return { success: false, error: err };
  }
};

const fetchEstimatesByProjectId = async (projectId: string): Promise<Result<Estimate[]>> => {
  try {
    const response = await fetchDataStrict({
      table: "estimates",
      // Unqualified on purpose: `estimates.project` makes the API require an explicit project
      // permission, which 403s users whose access comes from a public project or regional admin.
      params: { search: [`project;${projectId}`, "AND", "estimates.deleted_at;NULL"] },
    });
    return { success: true, data: response?.data || [] };
  } catch (error) {
    const err = handleError(error, { table: "estimates", context: "fetchEstimatesByProjectId" });
    return { success: false, error: err };
  }
};

const fetchVMCategoriesByProjectIdUncached = async (projectId: string): Promise<Result<Alternate[]>> => {
  try {
    const response = await fetchDataStrict({
      table: "projects",
      id: projectId,
      subRoute: "value-management-categories",
    });
    const vmCategories = response?.data || [];
    return { success: true, data: vmCategories };
  } catch (error) {
    const err = handleError(error, {
      table: "projects",
      subRoute: "value-management-categories",
      context: "fetchVMCategoriesByProjectId",
    });
    return { success: false, error: err };
  }
};

const fetchProjectById = async (projectId: string, params?: { populate?: string[] }): Promise<Result<Project>> => {
  try {
    const apiParams: Record<string, string | number | string[]> = params?.populate ? { populate: params.populate } : {};
    // Strict: tabulateProjectReportData must distinguish "fetch failed" (keep existing row)
    // from "empty response" — plain fetchData swallows failures into undefined
    const response = await fetchDataStrict({
      table: "projects",
      id: projectId,
      params: apiParams,
    });
    return { success: true, data: response?.data };
  } catch (error) {
    const err = handleError(error, { table: "projects", params, context: "fetchProjectById" });
    return { success: false, error: err };
  }
};

const vmCategories = defineCachedResourceById(fetchVMCategoriesByProjectIdUncached);

const fetchVMCategoriesByProjectId = vmCategories.get;

export const vmCategoryCaches: CacheHandle[] = [vmCategories];

export { fetchAllProjects, fetchEstimatesByProjectId, fetchVMCategoriesByProjectId, fetchProjectById };
